import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

export interface RadioCardOption {
  value: string;
  title: string;
  description?: string;
}

interface RadioCardGroupProps {
  options: RadioCardOption[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** Number of columns on sm+ screens (single column on xs). Default 2. */
  columns?: number;
  error?: boolean;
  disabled?: boolean;
}

/**
 * Segmented radio-card selector — a spacious, mobile-responsive alternative
 * to a dropdown. Each card shows a title + optional description, with a clear
 * selected state (accent border, tinted background, check icon). Colours come
 * from the MUI theme so it matches the Step Up design system automatically.
 */
const RadioCardGroup: React.FC<RadioCardGroupProps> = ({
  options,
  value,
  onChange,
  columns = 2,
  error = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <Box
      role="radiogroup"
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, 1fr)` },
      }}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <Box
            key={opt.value}
            role="radio"
            aria-checked={selected}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onChange(opt.value)}
            onKeyDown={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onChange(opt.value);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.75,
              borderRadius: "12px",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              userSelect: "none",
              border: `1.5px solid ${
                selected ? primary : error ? theme.palette.error.main : "rgba(0,0,0,0.12)"
              }`,
              bgcolor: selected ? alpha(primary, 0.06) : "#fff",
              boxShadow: selected ? `0 1px 8px ${alpha(primary, 0.14)}` : "none",
              transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
              "&:hover": disabled
                ? {}
                : { borderColor: primary, bgcolor: alpha(primary, selected ? 0.08 : 0.03) },
              "&:focus-visible": {
                outline: `2px solid ${alpha(primary, 0.5)}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box sx={{ color: selected ? primary : "text.disabled", display: "flex", mt: "1px" }}>
              {selected ? (
                <CheckCircleIcon sx={{ fontSize: 20 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: selected ? primary : "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {opt.title}
              </Typography>
              {opt.description && (
                <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.35, lineHeight: 1.4 }}>
                  {opt.description}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default RadioCardGroup;
